import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Image, Button, Select } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import SearchBar from "../../components/searchBar";
import ColumnVisibility from "../../components/columnVisibility";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import DateRangeFilter from "../../components/dateRangeFilter";
import { useGetCreditNotesQuery } from "../../services/creditNote";
import { useDeleteRefundMutation } from "../../services/refund";
import usePermissions from "../../hooks/usePermissions";
import { getCreditNoteColumns, CreditNoteRecord } from "./columns";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface DateRangeValue { start: Date; end: Date; }

interface VisibleColumns {
  creditNoteNumber: boolean;
  againstInvoice:   boolean;
  paymentRefundDate: boolean;
  branchName:       boolean;
  customerName:     boolean;
  customerMobile:   boolean;
  serviceType:      boolean;
  trainerName:      boolean;
  salesPersonName:  boolean;
  creditor:         boolean;
  packageName:      boolean;
  startDate:        boolean;
  expiryDate:       boolean;
  packagePrice:     boolean;
  refundAmount:     boolean;
  remark:           boolean;
  view:             boolean;
  actions:          boolean;
}

const serviceTypeOptions = [
  { value: 'all',               label: 'All Services'    },
  { value: 'personal_training', label: 'Personal Training' },
  { value: 'pilates',           label: 'Pilates'          },
  { value: 'therapy',           label: 'Therapy'          },
  { value: 'ems',               label: 'EMS'              },
  { value: 'paid_locker',       label: 'Paid Locker'      },
  { value: 'mma',               label: 'MMA'              },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const CreditNote = () => {
  const { hasPermission } = usePermissions();
  const branchId = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(10);
  const [searchText,  setSearchText]  = useState("");
  // Date range is display-only until the API confirms startDate/endDate params for this list.
  const [dateRange,   setDateRange]   = useState<DateRangeValue | null>(null);
  const [remarkModal, setRemarkModal] = useState<CreditNoteRecord | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");

  const { data: creditNotesResp, isFetching, refetch } = useGetCreditNotesQuery({
    search:      searchText || undefined,
    branchId:    selectedBranchId && selectedBranchId !== 'all' ? selectedBranchId : undefined,
    serviceType: serviceTypeFilter !== 'all' ? serviceTypeFilter : undefined,
    page,
    limit,
  });

  const [deleteRefund] = useDeleteRefundMutation();

  const apiData = creditNotesResp as any;
  const creditNotes = useMemo<CreditNoteRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows;
  }, [apiData]);

  const handleViewRemark = (record: CreditNoteRecord) => setRemarkModal(record);

  const handleDelete = (record: CreditNoteRecord) => {
    Modal.confirm({
      title:      'Delete Credit Note',
      content:    `Are you sure you want to delete credit note "${record.creditNoteNumber}"?`,
      okText:     'Delete',
      okType:     'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        if (!record.invoiceId?._id) return;
        try {
          await deleteRefund(record.invoiceId._id).unwrap();
        } catch {
          /* error toast handled globally by badRequestHandler */
        }
      },
    });
  };

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    creditNoteNumber: true,
    againstInvoice:   true,
    paymentRefundDate: true,
    branchName:       true,
    customerName:     true,
    customerMobile:   true,
    serviceType:      true,
    trainerName:      true,
    salesPersonName:  true,
    creditor:         true,
    packageName:      true,
    startDate:        true,
    expiryDate:       true,
    packagePrice:     true,
    refundAmount:     true,
    remark:           true,
    view:             true,
    actions:          true,
  });

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey as keyof VisibleColumns],
    }));
  };

  // Deleting a credit note is expected to require the same permission that
  // guards creating one, until a dedicated CREDIT_NOTE_DELETE key is confirmed.
  const allColumns = getCreditNoteColumns(handleViewRemark, handleDelete, hasPermission('CREDIT_NOTE_CREATE'));
  const columns = allColumns.filter(col => visibleColumns[col.key as keyof VisibleColumns]);

  return (
    <div className="credit-note-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(v: string) => { setSearchText(v); setPage(1); }}
            placeholder="Search by name, mobile or credit note no..."
          />
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
          />
        </div>
        <div className="right-col">
          <Select
            value={serviceTypeFilter}
            onChange={(v: string) => { setServiceTypeFilter(v); setPage(1); }}
            style={{ height: 41, minWidth: 160 }}
            options={serviceTypeOptions}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} />
          <ColumnVisibility
            columns={allColumns}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="credit-note-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={creditNotes}
          loading={isFetching}
          pagination={false}
          rowKey={(record: CreditNoteRecord) => record._id as string}
          scroll={{ x: 2050 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <Modal
        open={!!remarkModal}
        onCancel={() => setRemarkModal(null)}
        footer={null}
        title="Remark"
        centered
      >
        {remarkModal?.remarksText && <p>{remarkModal.remarksText}</p>}
        {remarkModal?.remarksImage && (
          <div style={{ textAlign: 'center' }}>
            <Image src={remarkModal.remarksImage} alt="Remark" style={{ maxWidth: '100%' }} />
          </div>
        )}
        {!remarkModal?.remarksText && !remarkModal?.remarksImage && <p>-</p>}
      </Modal>
    </div>
  );
};

export default CreditNote;
